<script setup>
import { computed, reactive, ref } from 'vue';
import EditSubscription from './EditSubscription.vue';
import utils from '../common/utils';
import { useAppStore } from '../store/app';
import { addDays, addWeeks, format } from 'date-fns';
import {
  returndate,
  getPaymentTypeDisplay,
  onToggleSubscription,
  enableUser,
  disableUser,
} from '../common/methods';
import { CREDITCARD_PAYMENT_TYPE_VALUE, EWAY_ONE_OFF_PAYMENT } from '../common/constants';
import Loader from './Loader.vue';
const props = defineProps({
  customers: {
    type: Array,
    required: true,
  },
  rowAction: {
    type: Function,
    required: true,
  },
  selectable: {
    type: Boolean,
  },
  product: {
    type: Object,
    required: true,
  },
});

const store = useAppStore();
const initCustomerData = ref({
  status: 1,
  subscriptionTypeName: 'ONE_OFF',
  paymentType: 'staff',
  subscriptionStartDate: addDays(
    new Date(),
    props.product?.freeTrialAvailable ? props.product?.daysFreeTrial : 0,
  ),
  // set default exp as two weeks
  expirationDate: addWeeks(
    addDays(new Date(), props.product?.freeTrialAvailable ? props.product?.daysFreeTrial : 0),
    2,
  ),
});

const editSubObj = ref({
  isNew: true,
  customer: initCustomerData.value,
});
const searchTxt = ref('');
const showEditSub = ref(false);

const tableHeaders = computed(() => {
  let headers = [
    {
      title: 'User Name',
      key: 'username',
    },
    {
      title: 'Email',
      key: 'email',
    },
    {
      title: 'Member Id',
      key: 'memberId',
    },
    {
      title: 'Name',
      key: 'name',
    },
    {
      title: 'Sub Name',
      key: 'subscriptionName',
    },
    {
      title: 'Sub Type',
      key: 'subscriptionTypeName',
      align: 'center',
    },
    {
      title: 'Member Since',
      key: 'subscriptionStartDate',
    },
    {
      title: 'Status',
      key: 'status',
    },
    {
      title: 'Free Trial',
      key: 'hasFreeTrial',
    },
    {
      title: 'Next Payment Date',
      key: 'expirationDate',
    },
    {
      title: 'Commercial',
      key: 'commercial',
    },
    {
      title: 'Payment Type',
      key: 'paymentType',
    },
    {
      title: 'Notes',
      key: 'subscriptionNotes',
    },
    {
      title: 'Actions',
      key: 'actions',
    },
  ];
  if (props.selectable) {
    headers.unshift({
      title: '',
      key: 'checkbox',
    });
  }
  return headers;
});

/**
 * Set customer edit data
 */
const onEditCustomerData = (customer) => {
  editSubObj.value.customer = { ...customer };
  editSubObj.value.isNew = false;
  showEditSub.value = true;
};

const onDisableUser = async (sub) => {
  store.appLoading = true;
  const res = await disableUser(sub);
  store.appLoading = false;
  onEditSubClose(res);
};

const onEnableUser = async (sub) => {
  store.appLoading = true;
  const res = await enableUser(sub);
  store.appLoading = false;
  onEditSubClose(res);
};

/**
 * Delete current customer
const onDeleteCurrentCustomer = ({ customer }) => {
  const message = `Are you sure you want to delete ${customer?.firstName} ${customer?.lastName}`;
  const isDelete = confirm(message);
  if (isDelete) {
    const customerId = customer?.id ?? customer?.customerId;
    store.deleteCurrentCustomer({ customerId });
  }
};
**/

const filteredCustomers = computed(() => {
  if (!searchTxt.value) return props.customers;
  return props.customers.filter((customer) => {
    return (
      customer.firstName?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.lastName?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.username?.toLowerCase().includes(searchTxt.value.toLowerCase()) ||
      customer.memberId?.toString()?.toLowerCase().includes(searchTxt.value.toLowerCase())
    );
  });
});

const onEditSubClose = (data) => {
  msgToUser.success = data?.success;
  msgToUser.message = data?.message;
  showEditSub.value = false;
  store.fetchCustomersByProductId({ productId: props.product?.id });
  msgToUser.show = data?.message ? true : false;
};

const onCacnelorEnableSub = async (sub) => {
  store.appLoading = true;
  const res = await onToggleSubscription(sub);
  store.appLoading = false;
  onEditSubClose(res);
};

const msgToUser = reactive({
  show: false,
  message: '',
  success: false,
});
</script>

<template>
  <div>
    <v-card class="pa-8">
      <v-card-title>
        <v-row class="mb-2">
          <v-col>
            <span>{{ customers.length }} subscriptions</span>
            <v-btn
              class="ml-4 ma-2"
              color="primary"
              prepend-icon="mdi-account-plus"
              @click="
                ((editSubObj.isNew = true), (editSubObj.customer = initCustomerData)),
                  (showEditSub = true) // Set to default
              ">
              New Subscription
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="3">
            <v-text-field
              density="compact"
              append-inner-icon="mdi-magnify"
              placeholder="Search by name or email..."
              single-line
              hide-details
              rounded
              v-model="searchTxt"
              variant="outlined"></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <Loader></Loader>
        <v-data-table
          :sort-by="[{ key: 'subscriptionStartDate', order: 'desc' }]"
          :headers="tableHeaders"
          :items="filteredCustomers"
          :loading="store.appLoading"
          item-value="customerId"
          item-title="email"
          hover>
          <template v-slot:[`item.name`]="{ item }">
            {{ item.firstName }} {{ item?.lastName }}
          </template>
          <template v-slot:[`item.paymentType`]="{ item }">
            {{ getPaymentTypeDisplay(item?.paymentType) }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip density="compact" :color="item?.status === 1 ? 'success' : 'red'">
              {{ item?.status === 1 ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
          <template v-slot:[`item.commercial`]="{ item }">
            <v-chip density="compact" :color="item?.commercial === 1 ? 'success' : 'grey-darken-1'">
              {{ item?.commercial === 1 ? 'Yes' : 'No' }}
            </v-chip>
          </template>
          <template v-slot:[`item.subscriptionStartDate`]="{ item }">
            {{ format(new Date(item.subscriptionStartDate), 'HH:mm:ss dd/MM/yyyy') }}
          </template>
          <template v-slot:[`item.subscriptionTypeName`]="{ item }">
            <div>{{ item.subscriptionTypeName }}</div>
            <v-chip
              class="mt-2"
              v-if="
                item.subscriptionTypeName == EWAY_ONE_OFF_PAYMENT &&
                item.subscriptionOneOffTransactionId !== '0'
              "
              density="compact"
              color="warning">
              Txn ID: {{ item.subscriptionOneOffTransactionId }}
            </v-chip>
          </template>
          <template v-slot:[`item.expirationDate`]="{ item }">
            {{ returndate({ date: item.expirationDate }) }}
          </template>
          <template v-slot:[`item.hasFreeTrial`]="{ item }">
            <v-chip density="compact" :color="item?.hasFreeTrial ? 'success' : 'grey-darken-1'">
              {{ item?.hasFreeTrial ? 'Applied' : 'N/A' }}
            </v-chip>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-tooltip text="Edit Subscription" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    @click="onEditCustomerData(item)"
                    variant="text"
                    icon="mdi-pencil-outline">
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="View Eway Recurring Transactions" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    :disabled="
                      !(
                        item.paymentType === CREDITCARD_PAYMENT_TYPE_VALUE &&
                        item.subscriptionTypeName === 'RECURRING'
                      )
                    "
                    v-bind="props"
                    @click="rowAction(item)"
                    variant="text"
                    icon="mdi-history">
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip
                :text="item.status ? 'Cancel Subscription' : 'Enable Subscription'"
                location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    @click="onCacnelorEnableSub(item)"
                    variant="text"
                    :disabled="
                      item.status === 0 && item.paymentType === CREDITCARD_PAYMENT_TYPE_VALUE
                    "
                    :color="item.status ? 'red darken-1' : 'success'"
                    :icon="
                      item.status ? 'mdi-account-cancel-outline' : 'mdi-account-reactivate-outline'
                    ">
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Disable User" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    @click="onDisableUser(item)"
                    variant="text"
                    color="red darken-1"
                    icon="mdi-cancel">
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Enable User" location="bottom">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    @click="onEnableUser(item)"
                    variant="text"
                    color="success"
                    icon="mdi-account">
                  </v-btn>
                </template>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <v-snackbar
      location="top center"
      v-model="msgToUser.show"
      :timeout="2000"
      :color="msgToUser.success ? 'success' : 'error'">
      {{ msgToUser.message }}
    </v-snackbar>
    <div class="table__not-found" v-if="utils.isEmpty(props.customers)">
      {{ store.appLoading ? 'Loading...' : ' No customers found...' }}
    </div>
  </div>
  <EditSubscription
    v-if="showEditSub"
    @on-close="onEditSubClose"
    :product="product"
    :data="editSubObj"
    :callback="() => {}"></EditSubscription>
</template>

<style lang="scss" scoped>
.subtable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
  text-align: left;

  &__head {
    thead {
      border-radius: 100px;
    }
    th {
      vertical-align: bottom;
      font-weight: 500;
    }
  }

  &__body {
    tr {
      box-shadow: rgba(0, 0, 0, 0.08) 0px 1px 1px 0px;
      transition: all 0.2s ease;
      height: 48px !important;
      cursor: pointer;
      &:hover {
        background: rgb(var(--v-theme-background-primary));
      }
    }

    td {
      vertical-align: middle;
      &:first-child {
        border-radius: 100px 0 0 100px;
        width: 60px;

        button {
          position: relative;
          left: 10px;
        }
      }

      &:last-child {
        border-radius: 0 100px 100px 0;
      }

      &:nth-child(2) {
        font-weight: 500;
      }
    }
  }

  &__not-found {
    text-align: center;
    font-style: italic;
    margin-top: 10px;
    text-transform: capitalize;
    margin-top: 30px;
  }
}
</style>
